import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React from "react";
import {
  equalityOperatorValue,
  safeJSONStringify,
  translateOperator,
} from "../../FilterBuilder/FilterBuilder.helpers";
import styles from "../../FilterBuilder/FilterBuilder.module.scss";
import {
  FilterBuilderFieldDefinition,
  FilterBuilderOperators,
} from "../../FilterBuilder/types";
import { Icon } from "../../Icon/Icon";
import { KeyValueList } from "../../KeyValueList/";
import { Tooltip } from "../../Tooltip";

const cx = classNames.bind(styles);

/**
 * Formats a filter value for display based on its type
 */
const formatValueForDisplay = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "null";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "object") {
    return safeJSONStringify([value, null, 2]);
  }

  return String(value);
};

export const FilterDisplayFieldValue = ({
  fieldDefinition,
  fieldKey,
  fieldValue,
}: {
  fieldDefinition?: FilterBuilderFieldDefinition;
  fieldKey: string;
  fieldValue: unknown;
}) => {
  // Calculate current operator and value
  const currentOperator = (
    isPlainObject(fieldValue)
      ? Object.keys(fieldValue as Record<string, unknown>)[0]
      : equalityOperatorValue
  ) as FilterBuilderOperators;

  const currentValue =
    currentOperator === equalityOperatorValue
      ? fieldValue
      : (fieldValue as Record<string, unknown>)[currentOperator];

  // When a field definition is not available, render as read-only with info
  if (!fieldDefinition) {
    return (
      <div className={cx("readonly-condition")}>
        <KeyValueList
          variant="shaded"
          className={cx("readonly-key-value")}
          data={[
            {
              title: fieldKey,
              value: (
                <div className={cx("readonly-value-container")}>
                  <div className={cx("readonly-value")}>
                    {safeJSONStringify([fieldValue, null, 2])}
                  </div>
                  <div className={cx("info")}>
                    <Tooltip content="This field is not defined in the field definitions">
                      <Icon
                        variant="info"
                        size={20}
                      />
                    </Tooltip>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    );
  }

  // Render the field with its definition
  const displayName = fieldDefinition.display_name;
  const operatorDisplay =
    currentOperator !== equalityOperatorValue
      ? translateOperator(currentOperator)
      : "equals";
  const valueDisplay = formatValueForDisplay(currentValue);

  return (
    <div className={cx("readonly-condition")}>
      <KeyValueList
        variant="shaded"
        className={cx("readonly-key-value")}
        data={[
          {
            title: displayName,
            value: (
              <div className={cx("readonly-value-container")}>
                <div className={cx("readonly-value")}>
                  {currentOperator !== equalityOperatorValue && (
                    <span className={cx("operator-label")}>
                      {operatorDisplay}{" "}
                    </span>
                  )}
                  {valueDisplay}
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
